<template>
  <TransitionRoot appear as="template" :show="isVisible">
    <Dialog as="div" class="relative z-[10000]" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4" @click.stop>
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-gray-800">
                <div class="flex items-center justify-between p-6 border-b border-gray-600">
                  <h3 class="text-lg font-semibold leading-6 text-white">
                    {{ isEditMode ? 'Edit Project' : 'Add Project' }}
                  </h3>
                  <button type="button" @click="closeDialog" :disabled="isLoading"
                    class="rounded-md text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <span class="sr-only">Close</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6">
                  <div class="space-y-6">
                    <!-- Images -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Images</label>
                      <div class="grid grid-cols-3 gap-4">
                        <div v-for="(preview, index) in previewImages" :key="index"
                          class="relative h-[100px] w-full rounded-lg bg-gray-700 border-2 border-gray-600 overflow-hidden">
                          <img :src="preview" alt="Image Preview" class="h-full w-full object-cover" />
                          <button @click="removeImage(index)"
                            class="absolute top-1 right-1 bg-red-600 text-white rounded-full h-6 w-6 flex items-center justify-center hover:bg-red-700">
                            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div
                          class="h-[100px] w-full rounded-lg bg-gray-700 border-2 border-gray-600 flex items-center justify-center cursor-pointer"
                          @click="$refs.fileInput.click()" :class="{ 'border-red-400': errors.images }">
                          <span class="text-gray-400 text-sm font-medium">Add Image</span>
                        </div>
                      </div>
                      <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/jpg" multiple
                        @change="handleImageChange" class="hidden" />
                      <p v-if="errors.images" class="mt-1 text-sm text-red-400">{{ errors.images }}</p>
                      <p v-if="imageUploading" class="mt-1 text-sm text-blue-400">Uploading images...</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Title *</label>
                      <input v-model="formData.title" type="text" maxlength="100"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.title }" />
                      <p v-if="errors.title" class="mt-1 text-sm text-red-400">{{ errors.title }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Duration *</label>
                      <input v-model="formData.duration" type="text" required
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.duration }" />
                      <p v-if="errors.duration" class="mt-1 text-sm text-red-400">{{ errors.duration }}</p>
                    </div>

                    <!-- Category Select -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Category *</label>
                      <select v-model="formData.categoryId"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.categoryId }">
                        <option value="" disabled>Select a category</option>
                        <option v-for="category in categories" :key="category.id" :value="category.id">
                          {{ category.name }}
                        </option>
                      </select>
                      <p v-if="errors.categoryId" class="mt-1 text-sm text-red-400">{{ errors.categoryId }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Number of Members</label>
                      <input v-model.number="formData.member" type="number" min="1"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.member }" />
                      <p v-if="errors.member" class="mt-1 text-sm text-red-400">{{ errors.member }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Demo Links</label>
                      <input v-model.number="formData.demoLink" type="text"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.demoLink }" />
                      <p v-if="errors.demoLink" class="mt-1 text-sm text-red-400">{{ errors.demoLink }}</p>
                    </div>


                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Description</label>
                      <textarea v-model="formData.description" rows="4" maxlength="500"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.description }" placeholder="Enter project description" />
                      <p v-if="errors.description" class="mt-1 text-sm text-red-400">{{ errors.description }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Problem / Challenge</label>
                      <textarea v-model="formData.problem" rows="4" maxlength="1000"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.problem }" placeholder="Describe the problem or challenge this project addresses" />
                      <p v-if="errors.problem" class="mt-1 text-sm text-red-400">{{ errors.problem }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Solution</label>
                      <textarea v-model="formData.solution" rows="4" maxlength="1000"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.solution }" placeholder="Describe how this project solves the problem" />
                      <p v-if="errors.solution" class="mt-1 text-sm text-red-400">{{ errors.solution }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">My Contribution</label>
                      <input v-model="myContributionInput" type="text" placeholder="Enter contribution and press Shift + Enter"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2"
                        @keyup="addMyContribution" @keydown.enter.prevent />
                      <p v-if="errors.myContribution" class="text-red-400 text-sm">{{ errors.myContribution }}</p>
                      <div class="flex flex-wrap gap-2">
                        <div v-for="(contribution, index) in formData.myContribution" :key="index"
                          class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                          <span>{{ contribution }}</span>
                          <button type="button" @click="removeMyContribution(index)" class="text-red-400 hover:text-red-300">
                            ×
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Frontend Technologies</label>
                      <input v-model="frontendsInput" type="text" placeholder="Enter frontend and press Shift + Enter"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2"
                        @keyup="addFrontend" @keydown.enter.prevent />
                      <p v-if="errors.frontends" class="text-red-400 text-sm">{{ errors.frontends }}</p>
                      <div class="flex flex-wrap gap-2">
                        <div v-for="(frontend, index) in formData.frontends" :key="index"
                          class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                          <span>{{ frontend }}</span>
                          <button type="button" @click="removeFrontend(index)" class="text-red-400 hover:text-red-300">
                            ×
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Backend Technologies</label>
                      <input v-model="backendsInput" type="text" placeholder="Enter backend and press Shift + Enter"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2"
                        @keyup="addBackend" @keydown.enter.prevent />
                      <p v-if="errors.backends" class="text-red-400 text-sm">{{ errors.backends }}</p>
                      <div class="flex flex-wrap gap-2">
                        <div v-for="(backend, index) in formData.backends" :key="index"
                          class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                          <span>{{ backend }}</span>
                          <button type="button" @click="removeBackend(index)" class="text-red-400 hover:text-red-300">
                            ×
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Databases</label>
                      <input v-model="databasesInput" type="text" placeholder="Enter database and press Shift + Enter"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2"
                        @keyup="addDatabase" @keydown.enter.prevent />
                      <p v-if="errors.databases" class="text-red-400 text-sm">{{ errors.databases }}</p>
                      <div class="flex flex-wrap gap-2">
                        <div v-for="(database, index) in formData.databases" :key="index"
                          class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                          <span>{{ database }}</span>
                          <button type="button" @click="removeDatabase(index)" class="text-red-400 hover:text-red-300">
                            ×
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Outcome / Impact</label>
                      <input v-model="outcomeInput" type="text" placeholder="Enter outcome/impact and press Shift + Enter"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2"
                        @keyup="addOutcome" @keydown.enter.prevent />
                      <p v-if="errors.outcome" class="text-red-400 text-sm">{{ errors.outcome }}</p>
                      <div class="flex flex-wrap gap-2">
                        <div v-for="(outcome, index) in formData.outcome" :key="index"
                          class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                          <span>{{ outcome }}</span>
                          <button type="button" @click="removeOutcome(index)" class="text-red-400 hover:text-red-300">
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                  <button @click="closeDialog" type="button" :disabled="isLoading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 border border-gray-500 rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                  <button @click="handleSubmit" type="button" :disabled="isLoading || imageUploading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    <template v-if="isLoading">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </template>
                    <template v-else>{{ isEditMode ? 'Update' : 'Create' }}</template>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { projectFirestore, projectStorage, projectAuth } from '@/firebase/config'; // Add projectAuth
import { doc, setDoc, getDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

const props = defineProps({
  isVisible: { type: Boolean, default: false },
  projectId: { type: String, default: null },
});

const emit = defineEmits(['onEmitHandleEvent']);

const isLoading = ref(false);
const imageUploading = ref(false);
const categories = ref([]);
const formData = ref({
  categoryId: '',
  title: '',
  duration: '',
  member: 1,
  description: '',
  demoLink: '',
  problem: '',
  solution: '',
  myContribution: [],
  frontends: [],
  backends: [],
  databases: [],
  outcome: [],
  imageURLs: [],
  imagePaths: [],
  status: true,
  createdAt: null, // Add createdAt
  createdBy: '',   // Add createdBy
  updatedAt: null, // Add updatedAt
  updatedBy: '',   // Add updatedBy
});
const errors = ref({
  categoryId: '',
  frontends: '',
  backends: '',
  databases: '',
});
const imageFiles = ref([]);
const previewImages = ref([]);
const frontendsInput = ref('');
const backendsInput = ref('');
const databasesInput = ref('');
const myContributionInput = ref('');
const outcomeInput = ref('');
const isEditMode = computed(() => !!props.projectId);

const resetForm = () => {
  formData.value = {
    categoryId: '',
    title: '',
    duration: '',
    member: 1,
    description: '',
    demoLink: '',
    problem: '',
    solution: '',
    myContribution: [],
    frontends: [],
    backends: [],
    databases: [],
    outcome: [],
    imageURLs: [],
    imagePaths: [],
    status: true,
    createdAt: null,
    createdBy: '',
    updatedAt: null,
    updatedBy: '',
  };
  errors.value = {};
  imageFiles.value = [];
  previewImages.value = [];
  frontendsInput.value = '';
  backendsInput.value = '';
  databasesInput.value = '';
  myContributionInput.value = '';
  outcomeInput.value = '';
};

const fetchCategories = async () => {
  try {
    const categoriesCol = collection(projectFirestore, 'categories');
    const categoriesSnapshot = await getDocs(categoriesCol);
    categories.value = categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name
    }));
  } catch (error) {
    console.error('Error fetching categories:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to load categories.');
  }
};

const closeDialog = () => {
  resetForm();
  emit('onEmitHandleEvent', 'close');
};

const handleImageChange = (event) => {
  const files = Array.from(event.target.files);
  if (!files.length) return;

  files.forEach(file => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      errors.value.images = 'Please upload only JPG or PNG images.';
      return;
    }
    errors.value.images = null;
    imageFiles.value.push(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      previewImages.value.push(e.target.result);
    };
    reader.readAsDataURL(file);
  });
};

const removeImage = (index) => {
  previewImages.value.splice(index, 1);
  imageFiles.value.splice(index, 1);
  if (isEditMode.value && formData.value.imageURLs[index]) {
    formData.value.imageURLs.splice(index, 1);
    formData.value.imagePaths.splice(index, 1);
  }
  errors.value.images = null;
};

const addItem = (inputRef, arrayKey, errorKey, validator = null) => (event) => {
  if (event.key === 'Enter' && event.shiftKey) {
    event.preventDefault();
    const value = inputRef.value.trim();
    if (!value) {
      errors.value[errorKey] = 'Input cannot be empty.';
      return;
    }
    if (formData.value[arrayKey].includes(value)) {
      errors.value[errorKey] = 'This item already exists.';
      return;
    }
    if (validator && !validator(value)) {
      errors.value[errorKey] = `Invalid ${errorKey} format.`;
      return;
    }
    formData.value[arrayKey].push(value);
    inputRef.value = '';
    errors.value[errorKey] = '';
  }
};

const removeItem = (arrayKey) => (index) => {
  formData.value[arrayKey].splice(index, 1);
};

const addFrontend = addItem(frontendsInput, 'frontends', 'frontends');
const addBackend = addItem(backendsInput, 'backends', 'backends');
const addDatabase = addItem(databasesInput, 'databases', 'databases');
const addMyContribution = addItem(myContributionInput, 'myContribution', 'myContribution');
const addOutcome = addItem(outcomeInput, 'outcome', 'outcome');

const removeFrontend = removeItem('frontends');
const removeBackend = removeItem('backends');
const removeDatabase = removeItem('databases');
const removeMyContribution = removeItem('myContribution');
const removeOutcome = removeItem('outcome');

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!formData.value.categoryId) {
    errors.value.categoryId = 'Category is required.';
    isValid = false;
  }

  if (!formData.value.title.trim()) {
    errors.value.title = 'Title is required.';
    isValid = false;
  }

  if (!formData.value.duration.trim()) {
    errors.value.duration = 'Duration is required.';
    isValid = false;
  }

  if (formData.value.member && formData.value.member < 1) {
    errors.value.member = 'Number of members must be at least 1.';
    isValid = false;
  }


  if (formData.value.description && formData.value.description.length > 500) {
    errors.value.description = 'Description cannot exceed 500 characters.';
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (isLoading.value || imageUploading.value) return;

  if (!validateForm()) return;

  isLoading.value = true;
  try {
    let imageURLs = isEditMode.value ? [...(formData.value.imageURLs || [])] : [];
    let imagePaths = isEditMode.value ? [...(formData.value.imagePaths || [])] : [];

    if (imageFiles.value.length > 0) {
      imageUploading.value = true;
      const uniqueId = props.projectId || Date.now().toString();

      // Delete old images if in edit mode and new images are uploaded
      if (isEditMode.value && (formData.value.imagePaths || []).length > 0) {
        for (const oldPath of (formData.value.imagePaths || [])) {
          const oldImageRef = storageRef(projectStorage, oldPath);
          await deleteObject(oldImageRef).catch((err) => console.warn('Old image deletion failed:', err));
        }
        imageURLs = [];
        imagePaths = [];
      }

      // Upload new images
      for (let i = 0; i < imageFiles.value.length; i++) {
        const file = imageFiles.value[i];
        const imagePath = `projects/${uniqueId}_${i}_${file.name}`;
        const imageRef = storageRef(projectStorage, imagePath);
        const snapshot = await uploadBytes(imageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        imageURLs.push(url);
        imagePaths.push(imagePath);
      }
    }

    const currentUser = projectAuth.currentUser;
    if (!currentUser) throw new Error('No authenticated user found.');

    const projectData = {
      categoryId: formData.value.categoryId,
      title: formData.value.title.trim(),
      demoLink: formData.value.demoLink.trim() || '',
      duration: formData.value.duration.trim() || '',
      member: Number(formData.value.member),
      description: formData.value.description.trim() || '',
      problem: formData.value.problem.trim() || '',
      solution: formData.value.solution.trim() || '',
      myContribution: formData.value.myContribution || [],
      frontends: formData.value.frontends || [],
      backends: formData.value.backends || [],
      databases: formData.value.databases || [],
      outcome: formData.value.outcome || [],
      imageURLs: imageURLs || [],
      imagePaths: imagePaths || [],
      status: true,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.uid, // Set updatedBy to current user's ID
    };

    if (!isEditMode.value) {
      projectData.createdAt = serverTimestamp();
      projectData.createdBy = currentUser.uid; // Set createdBy to current user's ID
    }

    // Use existing projectId for updates, create new ID only for new projects
    const projectRef = doc(projectFirestore, 'projects', isEditMode.value ? props.projectId : doc(collection(projectFirestore, 'projects')).id);
    await setDoc(projectRef, projectData, { merge: true });

    emit('onEmitHandleEvent', isEditMode.value ? 'updated' : 'created');
    resetForm();
  } catch (error) {
    console.error('Error saving project:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to save project. Please try again.');
  } finally {
    isLoading.value = false;
    imageUploading.value = false;
  }
};

const fetchProject = async () => {
  if (!props.projectId) {
    resetForm();
    return;
  }
  try {
    const projectRef = doc(projectFirestore, 'projects', props.projectId);
    const projectDoc = await getDoc(projectRef);
    if (projectDoc.exists()) {
      formData.value = {
        ...formData.value,
        ...projectDoc.data(),
        imageURLs: projectDoc.data().imageURLs || [],
        imagePaths: projectDoc.data().imagePaths || [],
        createdAt: projectDoc.data().createdAt || null,
        createdBy: projectDoc.data().createdBy || '',
        updatedAt: projectDoc.data().updatedAt || null,
        updatedBy: projectDoc.data().updatedBy || '',
      };
      previewImages.value = [...(formData.value.imageURLs || [])];
    } else {
      emit('onEmitHandleEvent', 'error', true, 'Project not found.');
    }
  } catch (error) {
    console.error('Error fetching project:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to load project data.');
  }
};

watch(
  () => props.projectId,
  (newProjectId) => {
    resetForm();
    if (newProjectId) {
      fetchProject();
    }
  },
  { immediate: true }
);

watch(
  () => props.isVisible,
  (newIsVisible) => {
    if (newIsVisible) {
      fetchCategories();
      if (!props.projectId) {
        resetForm();
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped>
input,
select,
textarea {
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #3b82f6;
}

button:disabled {
  cursor: not-allowed;
}
</style>